import { useState, useEffect } from 'react';
import { FiX, FiPackage, FiTrendingUp, FiTrendingDown, FiFileText } from 'react-icons/fi';
import { productsAPI } from '../../services/api';

const ItemHistoryModal = ({ product, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchHistory = async () => {
      try {
        setLoading(true);
        const res = await productsAPI.getStockHistory(product.id, { limit: 100 });
        if (!cancelled) setHistory(res.data.data);
      } catch (error) {
        console.error('Error fetching item history:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchHistory();
    return () => { cancelled = true; };
  }, [product.id]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="text-lg font-bold text-gray-800">{product.name}</h3>
            <p className="text-sm text-gray-500">
              {product.item_code}{product.category_name ? ` · ${product.category_name}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition">
            <FiX className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading history...</p>
          </div>
        ) : !history ? (
          <div className="text-center py-16 text-gray-600">Failed to load item history.</div>
        ) : (
          <div className="overflow-y-auto flex-1">
            {/* Stock balance */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5 border-b bg-gray-50">
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <p className="text-xs text-gray-500">Office</p>
                <p className="text-xl font-bold text-gray-800">{history.stock_balance.office}</p>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <p className="text-xs text-gray-500">Godown</p>
                <p className="text-xl font-bold text-gray-800">{history.stock_balance.godown}</p>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <p className="text-xs text-gray-500">Factory</p>
                <p className="text-xl font-bold text-gray-800">{history.stock_balance.factory}</p>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <p className="text-xs text-gray-500">Total Stock</p>
                <p className="text-xl font-bold text-blue-600">{history.stock_balance.total} {history.product.unit}</p>
              </div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-full"><FiTrendingUp className="w-4 h-4 text-green-600" /></div>
                <div>
                  <p className="text-xs text-gray-500">Total Stock In</p>
                  <p className="text-base font-semibold text-gray-800">{history.summary.total_stock_in}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-full"><FiTrendingDown className="w-4 h-4 text-red-600" /></div>
                <div>
                  <p className="text-xs text-gray-500">Total Stock Out</p>
                  <p className="text-base font-semibold text-gray-800">{history.summary.total_stock_out}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-full"><FiFileText className="w-4 h-4 text-blue-600" /></div>
                <div>
                  <p className="text-xs text-gray-500">Invoiced Sales</p>
                  <p className="text-base font-semibold text-gray-800">
                    {history.summary.invoice_linked_count} inv. / {history.summary.invoice_linked_quantity} {history.product.unit}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-full"><FiPackage className="w-4 h-4 text-purple-600" /></div>
                <div>
                  <p className="text-xs text-gray-500">Calculated Balance</p>
                  <p className="text-base font-semibold text-gray-800">{history.summary.calculated_balance}</p>
                </div>
              </div>
            </div>

            {/* Transactions timeline */}
            <div className="p-5">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Transaction History</h4>
              {history.transactions.length === 0 ? (
                <p className="text-sm text-gray-500 py-8 text-center">No stock transactions found for this item.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {history.transactions.map((tx, idx) => (
                        <tr key={`${tx.type}-${tx.id}-${idx}`} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                            {tx.date ? new Date(tx.date).toLocaleDateString('en-IN') : '-'}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              tx.type === 'IN' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {tx.type === 'IN' ? 'Stock In' : 'Stock Out'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-800">{tx.description}</td>
                          <td className="px-3 py-2 text-gray-600 capitalize">{tx.location}</td>
                          <td className={`px-3 py-2 font-semibold ${tx.type === 'IN' ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.change}
                          </td>
                          <td className="px-3 py-2 text-gray-600">{tx.created_by_name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemHistoryModal;
